import { assertUnreachable, isDefined, isEmpty } from '@sgme/fp';
import { VStack } from '@sgme/ui';
import clsx from 'clsx';
import { type ComponentProps, useMemo } from 'react';
import { FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl';

import type { Currency } from '@/types/currency.ts';

type Formatting = 'date' | 'rate' | 'points' | 'amount' | 'none';

type Props = {
  labelI18NKey: MessageDescriptor['id'];
  value: string | number | null | undefined;
  labelI18NValues?: ComponentProps<typeof FormattedMessage>['values'];
  currency?: Currency;
  formatting?: Formatting;
  singleColumn?: boolean;
  className?: string;
  minWidth?: string;

  labelDataE2e?: string;
  valueDataE2e?: string;
};

export const BlotterDetailsField = ({
  labelI18NKey,
  labelI18NValues,
  value,
  currency,
  singleColumn = false,
  className,
  formatting = 'none',
  minWidth,
  labelDataE2e,
  valueDataE2e,
}: Props) => {
  const { formatDate, formatNumber } = useIntl();

  const formatter = useMemo<(value: string | number) => string>(() => {
    switch (formatting) {
      case 'none':
        return (value) => value.toString();
      case 'date':
        return (value) =>
          isDefined(value) && value !== '' && value !== 'null' ? formatDate(value, { dateStyle: 'medium' }) : '';
      case 'rate':
        return (value) => (typeof value === 'number' ? formatNumber(value, { maximumFractionDigits: 10 }) : value);
      case 'points':
        return (value) => (typeof value === 'number' ? formatNumber(value, { signDisplay: 'always' }) : value);
      case 'amount':
        return (value) => {
          if (isEmpty(currency)) {
            return value.toString();
          }
          const numberValue = typeof value === 'string' ? Number.parseFloat(value) : value;
          return formatNumber(numberValue, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 10,
            currency,
            style: 'currency',
            currencyDisplay: 'code',
          });
        };
      default:
        return assertUnreachable(formatting, `Unknown value: ${formatting}`);
    }
  }, [currency, formatting, formatDate, formatNumber]);

  const formattedValue = isDefined(value) ? formatter(value) : '-';
  return (
    <VStack className={clsx('text-nowrap mb-1', singleColumn ? 'col-12' : 'col-xl-6 col-lg-12', className)}>
      <div className="text-secondary fw-medium" data-e2e={labelDataE2e}>
        <FormattedMessage id={labelI18NKey} values={labelI18NValues} />
      </div>
      <div className="fw-bold" style={{ minWidth }} data-e2e={valueDataE2e}>
        {formattedValue}
      </div>
    </VStack>
  );
};
